import { loadGoogleMapsAPI } from '@services/google/google-maps';
import { debounce } from 'lodash';
import { initAutocomplete } from '../ui/autocomplete';
import { initTagsUI } from '../ui/tags';
import { DEFINITIONS } from '../utils/constants';
import { applyFiltersAndUpdate } from './filters';
import { initStores } from './init-stores';

/**
 * @description Loads Google Maps API, initializes the map, stores, markers, tags UI, and search/autocomplete
 * Sets up event listeners for map idle events to keep the store list and tags in sync
 * @param {HTMLElement} context - The container element for the store locator section
 * @returns {Promise}
 */
export const initMap = async (context) => {
  let map;
  let userPosition = { coords: null, isError: false };
  const storeLocatorSettings = window.dAgency?.storeLocator || {};
  const mapOptions = storeLocatorSettings?.options?.map || {};
  const autocompleteOptions = storeLocatorSettings?.options?.autocomplete || {};

  // App state
  const state = {
    allStores: [],
    tags: [],
    activeTags: new Set(),
    markers: {}, // Store markers by ID
    clusterer: null, // MarkerClusterer instance
    lastVisibleIds: new Set(),
  };

  // Get user location with improved error handling for macOS
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation is not supported by this browser');
        resolve({ coords: null, isError: true });
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds timeout
        maximumAge: 0, // Don't use cached position
      };

      // Set a timeout to prevent infinite waiting
      const timeoutId = setTimeout(() => {
        console.warn('Geolocation request timed out, proceeding without user position');
        resolve({ coords: null, isError: true });
      }, 12000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          resolve({
            coords: { lat: position.coords.latitude, lng: position.coords.longitude },
            isError: false,
          });
        },
        (error) => {
          clearTimeout(timeoutId);

          // Handle different error types
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.warn('User denied geolocation permission');
              break;
            case error.POSITION_UNAVAILABLE:
              console.warn('Position information is unavailable (kCLErrorLocationUnknown on macOS)');
              break;
            case error.TIMEOUT:
              console.warn('Geolocation request timed out');
              break;
            default:
              console.warn('An unknown geolocation error occurred:', error.message);
          }

          resolve({ coords: null, isError: true });
        },
        options,
      );
    });
  };

  // Get user location asynchronously
  const locationPromise = getUserLocation();

  const loader = loadGoogleMapsAPI({
    key: storeLocatorSettings?.apiKey ?? '',
    v: 'weekly',
    region: mapOptions?.region ?? 'US',
    language: mapOptions?.language ?? 'en',
  });

  try {
    await loader;
  } catch (error) {
    return console.error('Error loading Google Maps API:', error);
  }

  const { Map } = await loader.importLibrary('maps');
  if (!Map) return console.error('Google Maps Map library is not loaded');

  map = new Map(context.querySelector(`.${DEFINITIONS.slug}__map`), {
    ...(mapOptions?.options || {}),
  });

  // Wait for user location (with timeout)
  userPosition = await locationPromise;

  // Load stores and markers
  const { stores, tags } = await initStores(context, { state, loader, map, userPosition });
  state.allStores = stores || [];
  state.tags = tags || [];

  // Init tags UI listeners once
  initTagsUI(context, { state, map, apply: applyFiltersAndUpdate });

  // Initial render
  applyFiltersAndUpdate(context, { state, map });

  // Keep list/tags in sync with map changes (debounced)
  const debouncedUpdate = debounce(() => {
    applyFiltersAndUpdate(context, { state, map });
  }, 150);

  map.addListener('idle', debouncedUpdate);

  if (autocompleteOptions?.enabled) {
    // --- Search / Autocomplete ---
    await initAutocomplete(context, {
      loader,
      map,
      mapOptions,
      userPosition,
      autocompleteOptions,
    });
  }
};
